import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { useAuth } from "./AuthProvider";
import {
  collection,
  addDoc,
  query,
  where,
  doc,
  updateDoc,
  setDoc,
  onSnapshot,
  Timestamp,
} from "firebase/firestore";
import { db } from "../firebase";
import { useVenue } from "./VenueProvider";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import {
  DndContext,
  DragOverlay,
  MouseSensor,
  TouchSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  useDraggable,
  useDroppable,
  pointerWithin,
} from "@dnd-kit/core";
import { CSS } from "@dnd-kit/utilities";

import { showAlert } from "../alertService";
import { Input, InputGroup } from "./input";
import { Select } from "./select";
import { Textarea } from "./textarea";
import {
  PlusIcon,
  Cog6ToothIcon,
  TrashIcon,
  EyeIcon,
  EyeSlashIcon,
  EllipsisVerticalIcon,
  XMarkIcon,
  PencilSquareIcon,
  ArrowRightIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  DocumentTextIcon,
  ArrowTopRightOnSquareIcon,
  MagnifyingGlassIcon,
  ChevronDoubleLeftIcon,
  ChevronDoubleRightIcon,
} from "@heroicons/react/20/solid";
import { Dialog, DialogTitle, DialogBody } from "./dialog";
import { logBitacora, BITACORA_ACTIONS } from "../utils/bitacora";
import LeadStats from "./LeadStats";
import LeadCard from "./leads/LeadCard";
import ClienteEventoDialog from "./directorio/ClienteEventoDialog";
import { camposLeadDesdeCliente, nombreCliente, contactoCliente } from "../utils/clientes";
import MiniYearCalendar from "./calendario/MiniYearCalendar";
import { useDayPopover } from "../hooks/useDayPopover";
import { esSoloLectura } from "../utils/roles";
import {
  EMPTY_LEAD,
  normalizeText,
  leadDesgloseTotal,
  esSinAsignar,
  ORIGEN_SIN_ASIGNAR,
  leadConFechasVivas,
} from "../utils/leadHelpers";
import {
  footprintDeEvento,
  footprintDesdeResumen,
  hayConflictoEntreFootprints,
  bloqueosPorEspacio,
} from "../utils/espacios";

/* ── dnd-kit wrappers: encapsulate hooks so they can be used inside .map() ── */
function ColumnDropZone({ id, children }) {
  const { setNodeRef, isOver } = useDroppable({ id });
  return children({ setNodeRef, isOver });
}

function LeadCardDraggable({ id, disabled, children }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id,
    disabled,
  });
  return children({ attributes, listeners, setNodeRef, transform, isDragging });
}

/* ── colour palette for columns (zinc base, coloured borders) ──────── */
const COLOR_PALETTE = {
  yellow: { dot: "bg-yellow-400", badge: "text-yellow-700 bg-yellow-100", hex: "#facc15" },
  blue:   { dot: "bg-blue-400",   badge: "text-blue-700 bg-blue-100",     hex: "#60a5fa" },
  green:  { dot: "bg-[#1b4d2e]",  badge: "text-[#1b4d2e] bg-[#1b4d2e1a]", hex: "#1b4d2e" },
  red:    { dot: "bg-red-400",    badge: "text-red-700 bg-red-100",       hex: "#f87171" },
  purple: { dot: "bg-purple-400", badge: "text-purple-700 bg-purple-100", hex: "#c084fc" },
  pink:   { dot: "bg-pink-400",   badge: "text-pink-700 bg-pink-100",     hex: "#f472b6" },
  orange: { dot: "bg-orange-400", badge: "text-orange-700 bg-orange-100", hex: "#fb923c" },
  gray:   { dot: "bg-zinc-400",   badge: "text-zinc-600 bg-zinc-100",     hex: "#a1a1aa" },
};

const COLOR_NAMES = Object.keys(COLOR_PALETTE);

/* ring classes used to highlight the selected colour swatch */
const SWATCH_RING = {
  yellow: "ring-yellow-500",
  blue: "ring-blue-500",
  green: "ring-[#1b4d2e]",
  red: "ring-red-500",
  purple: "ring-purple-500",
  pink: "ring-pink-500",
  orange: "ring-orange-500",
  gray: "ring-zinc-500",
};

/* dot colours re-used as swatch fills */
const SWATCH_BG = {
  yellow: "bg-yellow-400",
  blue: "bg-blue-400",
  green: "bg-[#1b4d2e]",
  red: "bg-red-400",
  purple: "bg-purple-400",
  pink: "bg-pink-400",
  orange: "bg-orange-400",
  gray: "bg-zinc-400",
};

const ACCENT = "#1b4d2e";

const DEFAULT_COLUMNS = [
  { key: "pendiente", label: "Pendiente", color: "yellow" },
  { key: "en_atencion", label: "En atención", color: "blue" },
  { key: "completado", label: "Completado", color: "green" },
  { key: "ganado", label: "Ganados", color: "green" },
  { key: "perdido", label: "Perdidos", color: "red", hidden: true },
];

/* Keys that cannot be deleted from column config */
const SYSTEM_COLUMN_KEYS = new Set(["pendiente", "ganado", "perdido"]);
/* Keys that cannot be hidden (always visible) */
const ALWAYS_VISIBLE_KEYS = new Set(["pendiente", "ganado"]);

function slugify(text) {
  return text
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_|_$/g, "");
}

/* ═══════════════════════════════════════════════════════════════
   Component
   ═══════════════════════════════════════════════════════════════ */
const Formularios = () => {
  const {
    selectedVenueId,
    events: contextEvents,
    selectedVenue,
    espaciosActivos,
    espaciosPorId,
    permiteEventosSimultaneos,
    clientes,
  } = useVenue();
  const { userInfo, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // isViewer decide qué se VE (notas ocultas al visor); soloLectura decide
  // qué se ESCRIBE (visor e inversionista, P-FRA-13).
  const isViewer = userInfo?.role === "viewer";
  const soloLectura = esSoloLectura(userInfo);

  /* ── tab & calendar state ─────────────────────────────────── */
  const [searchParams, setSearchParams] = useSearchParams();
  const VALID_TABS = ["pipeline", "calendario", "stats"];
  const tabFromQuery = searchParams.get("view");
  const leadsTab = VALID_TABS.includes(tabFromQuery)
    ? tabFromQuery
    : location.state?.tab === "calendario"
      ? "calendario"
      : "pipeline";
  const setLeadsTab = useCallback((next) => {
    setSearchParams((prev) => {
      const sp = new URLSearchParams(prev);
      if (next === "pipeline") sp.delete("view");
      else sp.set("view", next);
      return sp;
    }, { replace: true });
  }, [setSearchParams]);
  // Promote legacy location.state?.tab into the query param on mount so that
  // refreshes and back/forward preserve the active tab.
  useEffect(() => {
    if (!tabFromQuery && location.state?.tab === "calendario") {
      setLeadsTab("calendario");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  /* ── pipeline filters (persisted in query params so they survive
        navigating into a lead and back) ─────────────────────────── */
  const qParam = searchParams.get("q") || "";
  const origenParam = searchParams.get("origen") || "all";
  const fechaParam = searchParams.get("fecha") || "all";
  const ordenParam = searchParams.get("orden") || "recientes";
  const espacioParam = searchParams.get("espacio") || "all";
  const setFilterParam = useCallback((key, value, defaultValue = "") => {
    setSearchParams((prev) => {
      const sp = new URLSearchParams(prev);
      if (!value || value === defaultValue) sp.delete(key);
      else sp.set(key, value);
      return sp;
    }, { replace: true });
  }, [setSearchParams]);
  const clearFilters = useCallback(() => {
    setSearchParams((prev) => {
      const sp = new URLSearchParams(prev);
      ["q", "origen", "fecha", "orden", "espacio"].forEach((k) => sp.delete(k));
      return sp;
    }, { replace: true });
  }, [setSearchParams]);
  const hasActiveFilters =
    qParam !== "" ||
    origenParam !== "all" ||
    fechaParam !== "all" ||
    espacioParam !== "all";
  // Carry the board's query params (view/q/origen/fecha/orden) into the lead
  // detail URL so LeadPage can send the user back to the same filtered board.
  const leadDetailPath = useCallback(
    (id, extra) => {
      const sp = new URLSearchParams(searchParams);
      if (extra) Object.entries(extra).forEach(([k, v]) => sp.set(k, v));
      const qs = sp.toString();
      return `/dashboard/leads/${id}${qs ? `?${qs}` : ""}`;
    },
    [searchParams]
  );

  // Resolvedor de nombres para las chips de espacio de las tarjetas. null si
  // el venue no maneja espacios: LeadCard entonces no pinta nada.
  const nombreEspacio = useMemo(
    () =>
      espaciosActivos.length > 0
        ? (id) => espaciosPorId[id]?.nombre || id
        : null,
    [espaciosActivos, espaciosPorId]
  );

  const [calendarYear, setCalendarYear] = useState(new Date().getFullYear());
  // Estado, posición y tiempos del popover viven en useDayPopover — los
  // comparte con la vista Año del calendario.
  const {
    popover: dayPopover,
    show: showPopover,
    scheduleHide: scheduleHidePopover,
    cancelHide: cancelHidePopover,
    close: closePopover,
  } = useDayPopover();

  /* ── forms state ──────────────────────────────────────────── */
  const [forms, setForms] = useState([]);
  const [updating, setUpdating] = useState(null);

  /* ── card menu ─────────────────────────────────────────────── */
  const [cardMenuId, setCardMenuId] = useState(null);

  /* ── create lead ──────────────────────────────────────────── */
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newLead, setNewLead] = useState(EMPTY_LEAD);
  // Un lead nace de un cliente del directorio: primero se elige (o se da de
  // alta) y luego se capturan tema, notas y origen. Sin cliente no hay lead
  // manual, que es lo que evita las fichas sueltas al convertir.
  const [eligiendoCliente, setEligiendoCliente] = useState(false);
  const [leadCliente, setLeadCliente] = useState(null);
  const [creating, setCreating] = useState(false);

  /* ── column config ────────────────────────────────────────── */
  const [columnsConfig, setColumnsConfig] = useState(DEFAULT_COLUMNS);
  const [showSettingsDialog, setShowSettingsDialog] = useState(false);
  const [editColumns, setEditColumns] = useState([]);
  const [savingColumns, setSavingColumns] = useState(false);

  /* ── return-from-LeadPage highlight ───────────────────────── */
  const [lastViewedId, setLastViewedId] = useState(null);
  const cardRefs = useRef({});

  /* ── column collapse (per-venue, localStorage) ────────────── */
  const [collapsedCols, setCollapsedCols] = useState({});
  const [activeDragId, setActiveDragId] = useState(null);
  const [dragWidth, setDragWidth] = useState(null);
  const isDragging = activeDragId !== null;
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 6 } }),
    useSensor(KeyboardSensor),
  );
  const collapseStorageKey = selectedVenueId
    ? `venueboard_collapsedLeadCols_${selectedVenueId}`
    : null;

  useEffect(() => {
    if (!collapseStorageKey) { setCollapsedCols({}); return; }
    try {
      const raw = localStorage.getItem(collapseStorageKey);
      const parsed = raw ? JSON.parse(raw) : {};
      // Drop persisted "manually collapsed" overrides so columns with content
      // always start expanded on load. Only keep "manually expanded" overrides
      // (used to keep an empty column visible).
      const filtered = Object.fromEntries(
        Object.entries(parsed).filter(([, v]) => v === false)
      );
      setCollapsedCols(filtered);
    } catch {
      setCollapsedCols({});
    }
  }, [collapseStorageKey]);

  /* ── horizontal-scroll fade indicators ────────────────────── */
  const kanbanScrollRef = useRef(null);
  const [scrollEdges, setScrollEdges] = useState({ left: false, right: false });
  const updateScrollEdges = useCallback(() => {
    const el = kanbanScrollRef.current;
    if (!el) { setScrollEdges({ left: false, right: false }); return; }
    setScrollEdges({
      left: el.scrollLeft > 4,
      right: el.scrollLeft + el.clientWidth < el.scrollWidth - 4,
    });
  }, []);

  /* ── existing origins for autocomplete ────────────────────── */
  const existingOrigins = useMemo(() => {
    const set = new Set();
    forms.forEach((f) => {
      if (f.origin && f.origin.toLowerCase() !== "venueboard" && !esSinAsignar(f.origin)) {
        set.add(f.origin);
      }
    });
    return [...set].sort();
  }, [forms]);

  /* ── real-time forms subscription ─────────────────────────── */
  useEffect(() => {
    if (!selectedVenueId) { setForms([]); return; }
    const q = query(
      collection(db, "venueContactForms"),
      where("venueId", "==", selectedVenueId),
    );
    const unsub = onSnapshot(q, (snapshot) => {
      const data = snapshot.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .filter((d) => !d.deleted);
      data.sort((a, b) => {
        const dateA = a.createdAt?.toDate?.() || new Date(0);
        const dateB = b.createdAt?.toDate?.() || new Date(0);
        return dateB - dateA;
      });
      setForms(data);
    });
    return unsub;
  }, [selectedVenueId]);

  /* ── return-from-LeadPage: highlight + scroll into view ─── */
  useEffect(() => {
    const fromLeadId = location.state?.fromLeadId;
    if (!fromLeadId || forms.length === 0) return;
    setLastViewedId(fromLeadId);
    // Wait a tick so the card is mounted, then scroll.
    requestAnimationFrame(() => {
      const node = cardRefs.current[fromLeadId];
      if (node?.scrollIntoView) {
        node.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    });
    const timeout = setTimeout(() => setLastViewedId(null), 2500);
    // Clear the location.state so a tab switch / re-render doesn't re-trigger.
    window.history.replaceState(
      { ...window.history.state, usr: { ...(location.state || {}), fromLeadId: null } },
      ""
    );
    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state?.fromLeadId, forms.length]);

  /* ── real-time column config subscription ────────────────── */
  useEffect(() => {
    if (!selectedVenueId) { setColumnsConfig(DEFAULT_COLUMNS); return; }
    const ref = doc(db, "venues", selectedVenueId, "settings", "leads");
    const unsub = onSnapshot(ref, (snap) => {
      if (snap.exists() && snap.data().columns?.length > 0) {
        const saved = snap.data().columns;
        // Ensure system columns are always present
        const keys = new Set(saved.map((c) => c.key));
        const merged = [...saved];
        if (!keys.has("pendiente")) merged.unshift({ key: "pendiente", label: "Pendiente", color: "yellow" });
        if (!keys.has("ganado")) merged.push({ key: "ganado", label: "Ganados", color: "green" });
        if (!keys.has("perdido")) merged.push({ key: "perdido", label: "Perdidos", color: "red", hidden: true });
        setColumnsConfig(merged);
      } else {
        setColumnsConfig(DEFAULT_COLUMNS);
      }
    });
    return unsub;
  }, [selectedVenueId]);

  /* ── handlers ─────────────────────────────────────────────── */
  const handleStatusChange = async (formId, newStatus) => {
    setUpdating(formId);
    try {
      const ref = doc(db, "venueContactForms", formId);
      await updateDoc(ref, { status: newStatus });
      showAlert("Estado actualizado", "success");
      const lead = forms.find((f) => f.id === formId);
      const oldStatus = lead?.status || "pendiente";
      const oldLabel = columnsConfig.find((c) => c.key === oldStatus)?.label || oldStatus;
      const newLabel = columnsConfig.find((c) => c.key === newStatus)?.label || newStatus;
      logBitacora(selectedVenueId, {
        action: BITACORA_ACTIONS.LEAD_STATUS_CHANGE,
        entity: "lead",
        entityId: formId,
        entityName: lead?.name || lead?.topic || "",
        details: { from: oldLabel, to: newLabel },
      }, userInfo, user);
    } catch (err) {
      showAlert("Error actualizando estado", "error");
      setUpdating(null);
      throw err;
    }
    setUpdating(null);
  };

  // Cuántas oportunidades MÁS tiene el cliente de cada lead: sus otros leads
  // y sus eventos que no nacieron de uno de esos leads (un lead convertido
  // y su evento son la misma oportunidad, no dos).
  const recurrenciaDe = useMemo(() => {
    const leadsPorCliente = {};
    const idsLeads = new Set();
    (forms || []).forEach((f) => {
      idsLeads.add(f.id);
      if (f.clienteId) leadsPorCliente[f.clienteId] = (leadsPorCliente[f.clienteId] || 0) + 1;
    });
    const eventosPorCliente = {};
    (contextEvents || []).forEach((e) => {
      if (!e.clienteId || e.status === "cancelado") return;
      if (e.convertedFromLeadId && idsLeads.has(e.convertedFromLeadId)) return;
      eventosPorCliente[e.clienteId] = (eventosPorCliente[e.clienteId] || 0) + 1;
    });
    return (form) => {
      if (!form?.clienteId) return null;
      return {
        leads: Math.max(0, (leadsPorCliente[form.clienteId] || 0) - 1),
        eventos: eventosPorCliente[form.clienteId] || 0,
      };
    };
  }, [forms, contextEvents]);

  // Llegar con ?nuevo=<clienteId> (el botón "Nuevo lead" del cliente) abre
  // el diálogo de crear lead con ese cliente ya puesto. El lead no existe
  // hasta que el owner lo guarde: nada de documentos vacíos en el tablero.
  useEffect(() => {
    const nuevoDe = searchParams.get("nuevo");
    if (!nuevoDe || !clientes?.length) return;
    const c = clientes.find((x) => x.id === nuevoDe);
    const next = new URLSearchParams(searchParams);
    next.delete("nuevo");
    setSearchParams(next, { replace: true });
    if (!c) return;
    setLeadCliente(c);
    setNewLead(EMPTY_LEAD);
    setShowCreateDialog(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, clientes]);

  const cerrarCreacion = () => {
    setShowCreateDialog(false);
    setEligiendoCliente(false);
    setNewLead(EMPTY_LEAD);
    setLeadCliente(null);
  };

  const handleCreateLead = async () => {
    if (!leadCliente?.id) {
      showAlert("Elige el cliente del lead", "error");
      return;
    }
    setCreating(true);
    try {
      const now = Timestamp.now();
      const firstKey = columnsConfig[0]?.key || "pendiente";
      // Nombre, correo y teléfono vienen del cliente, no se recapturan: el
      // lead es una copia de la ficha, y la ficha es la verdad.
      const delCliente = camposLeadDesdeCliente(leadCliente);
      const data = {
        clienteId: leadCliente.id,
        name: delCliente.name,
        phone: delCliente.phone,
        userEmail: delCliente.userEmail,
        topic: newLead.topic.trim(),
        notes: newLead.notes.trim(),
        status: firstKey,
        country: newLead.country,
        venueId: selectedVenueId,
        createdAt: now,
        updatedAt: now,
      };
      // Siempre explícito: omitirlo lo haría pasar por lead del formulario
      // público de Venueboard, que es lo que significa el campo ausente.
      data.origin = newLead.origin.trim() || ORIGEN_SIN_ASIGNAR;
      const docRef = await addDoc(collection(db, "venueContactForms"), data);
      showAlert("Lead creado correctamente", "success");
      logBitacora(selectedVenueId, {
        action: BITACORA_ACTIONS.LEAD_CREATE,
        entity: "lead",
        entityId: docRef.id,
        entityName: delCliente.name,
        details: { clienteId: leadCliente.id, clienteNombre: nombreCliente(leadCliente) },
      }, userInfo, user);
      cerrarCreacion();
    } catch {
      showAlert("Error creando lead", "error");
    }
    setCreating(false);
  };

  const handleSaveColumns = async () => {
    const usedKeys = new Set();
    const cleaned = editColumns
      .filter((c) => c.label.trim())
      .map((c) => {
        let key = c.key || slugify(c.label);
        while (usedKeys.has(key)) key += "_2";
        usedKeys.add(key);
        return { ...c, label: c.label.trim(), key };
      });
    if (cleaned.length === 0) {
      showAlert("Debe haber al menos una columna", "error");
      return;
    }
    setSavingColumns(true);
    try {
      const ref = doc(db, "venues", selectedVenueId, "settings", "leads");
      await setDoc(ref, { columns: cleaned }, { merge: true });
      setColumnsConfig(cleaned);
      setShowSettingsDialog(false);
      showAlert("Columnas actualizadas", "success");
      logBitacora(selectedVenueId, {
        action: BITACORA_ACTIONS.LEAD_EDIT_COLUMNS,
        entity: "lead_columns",
        entityId: selectedVenueId,
        entityName: "Columnas de leads",
        details: {},
      }, userInfo, user);
    } catch {
      showAlert("Error guardando columnas", "error");
    }
    setSavingColumns(false);
  };


  /* ── search / origin / date filtering ─────────────────────── */
  const filteredForms = useMemo(() => {
    let list = forms;
    if (origenParam !== "all") {
      if (origenParam === "__venueboard__") {
        list = list.filter((f) => !f.origin || f.origin.toLowerCase() === "venueboard");
      } else {
        list = list.filter((f) => f.origin === origenParam);
      }
    }
    if (fechaParam !== "all") {
      const now = new Date();
      let cutoff = null;
      if (fechaParam === "7d") cutoff = new Date(now.getTime() - 7 * 864e5);
      else if (fechaParam === "30d") cutoff = new Date(now.getTime() - 30 * 864e5);
      else if (fechaParam === "90d") cutoff = new Date(now.getTime() - 90 * 864e5);
      else if (fechaParam === "anio") cutoff = new Date(now.getFullYear(), 0, 1);
      if (cutoff) {
        list = list.filter((f) => {
          const d = f.createdAt?.toDate?.();
          return d && d >= cutoff;
        });
      }
    }
    if (espacioParam !== "all") {
      // "Sin definir" = leads que aún no declaran espacio (les sirve
      // cualquiera). Un espacio concreto trae los que lo piden y también los
      // que piden un espacio que lo contiene o es incompatible con él.
      if (espacioParam === "__sin__") {
        list = list.filter((f) => !(f.espaciosIds || []).length);
      } else {
        const entry = espaciosPorId[espacioParam];
        // Sin entry el filtro queda INERTE (como "Todos"): el param de la URL
        // sobrevive al switch de venue, y un id ajeno con footprint null
        // (= todo el venue) filtraría en silencio mientras el Select muestra
        // "Todos los espacios".
        if (entry) {
          const fpFiltro = new Set(entry.unidades || [entry.id]);
          list = list.filter((f) => {
            const ids = f.espaciosIds || [];
            if (ids.length === 0) return false;
            return hayConflictoEntreFootprints(
              fpFiltro,
              footprintDesdeResumen(ids, espaciosPorId),
              espaciosPorId
            );
          });
        }
      }
    }
    const q = normalizeText(qParam.trim());
    if (q) {
      list = list.filter((f) =>
        [f.name, f.topic, f.userEmail, f.phone, f.message, isViewer ? null : f.notes, f.origin]
          .some((v) => v && normalizeText(v).includes(q))
      );
    }
    return list;
  }, [forms, qParam, origenParam, fechaParam, espacioParam, espaciosPorId, isViewer]);

  /* ── build visible columns ────────────────────────────────── */
  const columns = useMemo(() => {
    return columnsConfig
      .filter((col) => !col.hidden || ALWAYS_VISIBLE_KEYS.has(col.key))
      .map((col) => {
        const colForms = filteredForms.filter((f) => (f.status || "pendiente") === col.key);
        if (ordenParam === "antiguos") {
          colForms.sort(
            (a, b) =>
              (a.createdAt?.toDate?.() || new Date(0)) - (b.createdAt?.toDate?.() || new Date(0))
          );
        } else if (ordenParam === "valor") {
          colForms.sort((a, b) => leadDesgloseTotal(b) - leadDesgloseTotal(a));
        }
        const total = colForms.reduce((sum, f) => sum + leadDesgloseTotal(f), 0);
        return { ...col, forms: colForms, total };
      });
  }, [columnsConfig, filteredForms, ordenParam]);

  /* ── collapsed-column helpers ─────────────────────────────── */
  const isColCollapsed = useCallback(
    (col) => {
      // Empty columns are always auto-collapsed. They stay collapsed during
      // drag too — the narrow column itself is a drop target, so no layout
      // shift happens when a drag starts.
      if (col.forms.length === 0) return true;
      const override = collapsedCols[col.key];
      if (typeof override === "boolean") return override;
      return false;
    },
    [collapsedCols]
  );

  const toggleColCollapse = useCallback(
    (col) => {
      const current = isColCollapsed(col);
      const next = { ...collapsedCols, [col.key]: !current };
      setCollapsedCols(next);
      if (collapseStorageKey) {
        try { localStorage.setItem(collapseStorageKey, JSON.stringify(next)); } catch {}
      }
    },
    [collapsedCols, isColCollapsed, collapseStorageKey]
  );

  /* ── recompute scroll edges when columns / collapsed state change ── */
  useEffect(() => {
    updateScrollEdges();
    const onResize = () => updateScrollEdges();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [columns, collapsedCols, isDragging, leadsTab, updateScrollEdges]);

  /* ── interest calendar data ─────────────────────────────── */
  const interestCalendarData = useMemo(() => {
    const map = new Map();
    const key = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    const ensure = (dateStr) => { if (!map.has(dateStr)) map.set(dateStr, { leadCount: 0, leads: [], events: [] }); return map.get(dateStr); };
    // leads: solo los que siguen apartando (ni convertidos, ni perdidos,
    // ni eliminados — utils/leadHelpers.leadConFechasVivas).
    forms.forEach((f) => {
      if (!leadConFechasVivas(f)) return;
      (f.interestDates || []).forEach((d) => {
        const s = d.startDate?.toDate ? d.startDate.toDate() : new Date(d.startDate);
        const e = d.endDate?.toDate ? d.endDate.toDate() : new Date(d.endDate);
        if (isNaN(s.getTime()) || isNaN(e.getTime())) return;
        const cur = new Date(s.getFullYear(), s.getMonth(), s.getDate());
        const end = new Date(e.getFullYear(), e.getMonth(), e.getDate());
        while (cur <= end) {
          const entry = ensure(key(cur));
          entry.leadCount += 1;
          entry.leads.push({ id: f.id, name: f.name || f.topic || "Lead", status: f.status || "pendiente" });
          cur.setDate(cur.getDate() + 1);
        }
      });
    });
    // events
    (contextEvents || []).forEach((ev) => {
      if (ev.status === "cancelado") return;
      const s = ev.startTime?.toDate ? ev.startTime.toDate() : new Date(ev.startTime);
      const e = ev.endTime?.toDate ? ev.endTime.toDate() : new Date(ev.endTime);
      if (isNaN(s.getTime()) || isNaN(e.getTime())) return;
      // Con eventos simultáneos apagados todo evento bloquea el venue entero,
      // así que se trata como footprint nulo (el sentinel de "todo").
      const fp = permiteEventosSimultaneos ? footprintDeEvento(ev) : null;
      const cur = new Date(s.getFullYear(), s.getMonth(), s.getDate());
      const end = new Date(e.getFullYear(), e.getMonth(), e.getDate());
      while (cur <= end) {
        ensure(key(cur)).events.push({
          id: ev.id,
          name: ev.nombre || ev.title || "Evento",
          fp,
        });
        cur.setDate(cur.getDate() + 1);
      }
    });

    // Ocupación por espacio de cada día: un evento en el Jardín no deja el
    // día "rojo" si el Salón sigue libre. Sin espacios el venue se comporta
    // como antes (cualquier evento llena el día).
    map.forEach((entry) => {
      if (entry.events.length === 0) return;
      if (espaciosActivos.length === 0) {
        entry.espaciosTotal = 0;
        entry.espaciosLibres = [];
        entry.lleno = true;
        return;
      }
      const bloqueos = bloqueosPorEspacio(
        entry.events.map((ev) => ({ fp: ev.fp, ref: ev })),
        espaciosActivos
      );
      entry.events.forEach((ev) => {
        ev.espacios = espaciosActivos
          .filter((esp) => (bloqueos.get(esp.id) || []).includes(ev))
          .map((esp) => esp.nombre);
      });
      entry.espaciosTotal = espaciosActivos.length;
      entry.espaciosLibres = espaciosActivos
        .filter((esp) => !bloqueos.has(esp.id))
        .map((esp) => esp.nombre);
      entry.lleno = entry.espaciosLibres.length === 0;
    });
    return map;
  }, [forms, contextEvents, espaciosActivos, permiteEventosSimultaneos]);

  // Contrato de día de MiniYearCalendar: el nivel de relleno es la ocupación
  // real por espacio y los leads interesados van como esquina amarilla, para
  // que las dos señales no compitan por el mismo color.
  const leadsDayInfo = useCallback(
    (iso) => {
      const info = interestCalendarData.get(iso);
      if (!info) return null;
      const conEvento = info.events.length > 0;
      const total = info.espaciosTotal || 0;
      const ocupados = conEvento
        ? total > 0
          ? total - info.espaciosLibres.length
          : 1
        : 0;
      const partes = [];
      if (info.leadCount > 0)
        partes.push(`${info.leadCount} lead${info.leadCount > 1 ? "s" : ""}`);
      if (conEvento)
        partes.push(
          total > 0
            ? `${ocupados} de ${total} espacios ocupados`
            : "Evento confirmado"
        );
      return {
        fill: conEvento ? (total > 0 ? ocupados / total : 1) : 0,
        color: "#f87171",
        accent: info.leadCount > 0 ? "#fbbf24" : undefined,
        count: info.leadCount,
        tooltip: partes.join(" · "),
        clickable: true,
      };
    },
    [interestCalendarData]
  );

  /* ── drag & drop ──────────────────────────────────────────── */
  const findColumnOfCard = useCallback(
    (cardId) => {
      const f = forms.find((x) => x.id === cardId);
      return f ? (f.status || "pendiente") : null;
    },
    [forms],
  );

  const handleDndEnd = async (event) => {
    setActiveDragId(null);
    setDragWidth(null);
    const { active, over } = event;
    if (!over) return;

    const draggedId = active.id;
    const sourceCol = findColumnOfCard(draggedId);
    if (!sourceCol) return;

    // over.id is either a column key (dropped on column body) or a card id (dropped on another card)
    const columnKeys = columnsConfig.map((c) => c.key);
    let destCol = over.id;
    if (!columnKeys.includes(destCol)) {
      destCol = findColumnOfCard(over.id);
    }
    if (!destCol || destCol === sourceCol) return;

    const draggedForm = forms.find((f) => f.id === draggedId);
    if (draggedForm?.convertedToEventId && destCol !== "ganado") return;

    setForms((prev) =>
      prev.map((f) => (f.id === draggedId ? { ...f, status: destCol } : f)),
    );
    try {
      await handleStatusChange(draggedId, destCol);
      setLastViewedId(draggedId);
      requestAnimationFrame(() => {
        const node = cardRefs.current[draggedId];
        if (node?.scrollIntoView) {
          node.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      });
      setTimeout(() => {
        setLastViewedId((current) => (current === draggedId ? null : current));
      }, 2500);
    } catch {
      setForms((prev) =>
        prev.map((f) => (f.id === draggedId ? { ...f, status: sourceCol } : f)),
      );
    }
  };

  /* ═══════════════════════════════════════════════════════════
     Render
     ═══════════════════════════════════════════════════════════ */
  return (
    <div className="flex flex-col h-[calc(100dvh-6.5rem)] lg:h-[calc(100dvh-4.5rem)] gap-4">
      {/* Capped header zone (header + tab pills + toolbar) */}
      <div className="w-full space-y-3 shrink-0">
      {/* Header */}
      <div className="flex items-center gap-2">
        <DocumentTextIcon className="h-5 w-5" style={{ color: ACCENT }} />
        <h1 className="text-lg sm:text-xl font-bold text-zinc-900 dark:text-white">Leads</h1>
        {selectedVenue && (
          <span className="text-sm text-zinc-400 font-normal hidden sm:inline">
            – {selectedVenue.name}
          </span>
        )}
      </div>

      {/* Tab pills + actions */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex gap-2">
          {[
            { key: "pipeline", label: "Pipeline" },
            { key: "calendario", label: "Calendario" },
            { key: "stats", label: "Estadísticas" },
          ].map((tab) => (
            <button
              key={tab.key}
              type="button"
              onClick={() => { setLeadsTab(tab.key); closePopover(); }}
              className={`px-4 py-2 rounded-lg text-sm font-semibold transition ${
                leadsTab === tab.key
                  ? "text-white shadow-sm"
                  : "text-zinc-500 bg-zinc-100 dark:bg-zinc-800 hover:bg-zinc-200 dark:hover:bg-zinc-700"
              }`}
              style={leadsTab === tab.key ? { backgroundColor: ACCENT } : undefined}
            >
              {tab.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          {!soloLectura && (
            <button
              onClick={() => {
                setEditColumns(columnsConfig.map((c) => ({ ...c })));
                setShowSettingsDialog(true);
              }}
              className="p-2 rounded-lg text-zinc-400 hover:text-zinc-600 hover:bg-zinc-100 dark:hover:bg-zinc-700 transition-colors"
              title="Configurar columnas"
            >
              <Cog6ToothIcon className="h-5 w-5" />
            </button>
          )}
          {!soloLectura && (
            <button
              onClick={() => setEligiendoCliente(true)}
              className="px-4 py-2 rounded-lg text-sm font-semibold text-white shadow-sm transition hover:opacity-90 flex items-center gap-1.5"
              style={{ backgroundColor: ACCENT }}
            >
              <PlusIcon className="h-4 w-4" />
              Crear lead
            </button>
          )}
        </div>
      </div>

      {/* Search + filter toolbar (pipeline only) */}
      {leadsTab === "pipeline" && (
        <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap sm:items-center">
          <div className="relative col-span-2 sm:col-auto sm:w-64">
            <InputGroup>
              <MagnifyingGlassIcon data-slot="icon" />
              <Input
                value={qParam}
                onChange={(e) => setFilterParam("q", e.target.value)}
                placeholder="Buscar leads..."
                aria-label="Buscar leads"
                className={qParam ? "[&_input]:pr-8" : undefined}
              />
            </InputGroup>
            {qParam && (
              <button
                type="button"
                onClick={() => setFilterParam("q", "")}
                className="absolute right-2 top-1/2 -translate-y-1/2 z-10 p-0.5 rounded text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-200"
                title="Limpiar búsqueda"
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            )}
          </div>
          <div className="min-w-0 sm:w-44">
            <Select
              value={origenParam}
              onChange={(e) => setFilterParam("origen", e.target.value, "all")}
              aria-label="Filtrar por origen"
            >
              <option value="all">Todos los orígenes</option>
              <option value="__venueboard__">Venueboard</option>
              {existingOrigins.map((o) => (
                <option key={o} value={o}>{o}</option>
              ))}
            </Select>
          </div>
          <div className="min-w-0 sm:w-44">
            <Select
              value={fechaParam}
              onChange={(e) => setFilterParam("fecha", e.target.value, "all")}
              aria-label="Filtrar por fecha"
            >
              <option value="all">Todas las fechas</option>
              <option value="7d">Últimos 7 días</option>
              <option value="30d">Últimos 30 días</option>
              <option value="90d">Últimos 90 días</option>
              <option value="anio">Este año</option>
            </Select>
          </div>
          {espaciosActivos.length > 0 && (
            <div className="min-w-0 sm:w-44">
              <Select
                value={espacioParam}
                onChange={(e) => setFilterParam("espacio", e.target.value, "all")}
                aria-label="Filtrar por espacio"
              >
                <option value="all">Todos los espacios</option>
                <option value="__sin__">Sin espacio definido</option>
                {espaciosActivos.map((esp) => (
                  <option key={esp.id} value={esp.id}>{esp.nombre}</option>
                ))}
              </Select>
            </div>
          )}
          <div className="min-w-0 sm:w-44">
            <Select
              value={ordenParam}
              onChange={(e) => setFilterParam("orden", e.target.value, "recientes")}
              aria-label="Ordenar leads"
            >
              <option value="recientes">Más recientes</option>
              <option value="antiguos">Más antiguos</option>
              <option value="valor">Mayor valor</option>
            </Select>
          </div>
          {hasActiveFilters && (
            <div className="flex items-center justify-end sm:justify-start gap-2 text-xs text-zinc-500 dark:text-zinc-400">
              <span className="font-medium whitespace-nowrap">
                {filteredForms.length} de {forms.length} leads
              </span>
              <button
                type="button"
                onClick={clearFilters}
                className="font-semibold hover:underline"
                style={{ color: ACCENT }}
              >
                Limpiar
              </button>
            </div>
          )}
        </div>
      )}
      </div>
      {/* end capped header zone */}

      {/* Kanban Board (full width — no cap) */}
      {leadsTab === "pipeline" && (
      <DndContext
        sensors={sensors}
        collisionDetection={pointerWithin}
        onDragStart={(event) => {
          setActiveDragId(event.active.id);
          const node = cardRefs.current[event.active.id];
          setDragWidth(node ? node.getBoundingClientRect().width : null);
        }}
        onDragCancel={() => { setActiveDragId(null); setDragWidth(null); }}
        onDragEnd={(event) => {
          if (soloLectura) {
            setActiveDragId(null);
            return;
          }
          handleDndEnd(event);
        }}
      >
        <div className="relative flex-1 min-h-0 min-w-0">
          <div
            ref={kanbanScrollRef}
            onScroll={updateScrollEdges}
            className="kanban-scroll flex gap-2 sm:gap-4 overflow-x-auto pb-2 sm:pb-4 h-full"
          >
          {columns.map((col) => {
            const styles = COLOR_PALETTE[col.color] || COLOR_PALETTE.gray;
            const collapsed = isColCollapsed(col);

            if (collapsed) {
              return (
                <ColumnDropZone key={col.key} id={col.key}>
                  {({ setNodeRef, isOver }) => (
                    <button
                      ref={setNodeRef}
                      type="button"
                      onClick={() => toggleColCollapse(col)}
                      className={`shrink-0 w-12 rounded-xl border transition-all flex flex-col items-center py-3 gap-3 ${
                        isOver
                          ? "border-[#1b4d2e] ring-2 ring-[#1b4d2e]/40"
                          : "border-zinc-200 dark:border-zinc-700/60 hover:brightness-95 dark:hover:brightness-125"
                      }`}
                      style={{ backgroundColor: styles.hex + "14" }}
                      title={`Expandir ${col.label}`}
                    >
                  <ChevronDoubleRightIcon className="h-3.5 w-3.5 text-zinc-400" />
                  <span className={`inline-block w-2 h-2 rounded-full ${styles.dot}`} />
                  <span
                    className="font-semibold text-xs tracking-wide uppercase text-zinc-600 dark:text-zinc-300 whitespace-nowrap"
                    style={{ writingMode: "vertical-rl", transform: "rotate(180deg)" }}
                  >
                    {col.label}
                  </span>
                  <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${styles.badge} mt-auto`}>
                    {col.forms.length}
                  </span>
                </button>
                  )}
                </ColumnDropZone>
              );
            }

            return (
              <div
                key={col.key}
                className="basis-[320px] grow shrink-0 rounded-xl border border-zinc-200 dark:border-zinc-700/60 bg-zinc-50/50 dark:bg-zinc-800/30 flex flex-col min-h-0 overflow-hidden"
              >
                {/* Column header */}
                <div
                  className="flex items-center justify-between gap-2 px-3 py-2 sm:px-4 sm:py-2.5"
                  style={{ backgroundColor: styles.hex + "14" }}
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className={`inline-block w-2 h-2 rounded-full shrink-0 ${styles.dot}`} />
                      <span className="font-semibold text-xs tracking-wide uppercase text-zinc-600 dark:text-zinc-300 truncate">
                        {col.label}
                      </span>
                    </div>
                    {col.total >= 1 && (
                      <div className="text-[10px] font-medium text-zinc-500 dark:text-zinc-400 mt-0.5 pl-4 truncate">
                        ${col.total.toLocaleString("es-MX", { maximumFractionDigits: 0 })}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${styles.badge}`}>
                      {col.forms.length}
                    </span>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); toggleColCollapse(col); }}
                      className="p-1 -mr-1 rounded text-zinc-400 hover:text-zinc-600 hover:bg-zinc-100 dark:hover:text-zinc-200 dark:hover:bg-zinc-700/50 transition-colors"
                      title="Colapsar columna"
                    >
                      <ChevronDoubleLeftIcon className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>

                {/* Droppable area */}
                <ColumnDropZone id={col.key}>
                  {({ setNodeRef, isOver }) => (
                    <div
                      ref={setNodeRef}
                      className={`column-scroll flex-1 min-h-0 p-2 space-y-2 overflow-y-scroll transition-colors rounded-b-xl ${
                        isOver ? "bg-zinc-100/60 dark:bg-zinc-700/20" : ""
                      }`}
                    >
                      {col.forms.length === 0 && !isOver && (
                        <p className="text-center text-xs text-zinc-400 dark:text-zinc-500 py-8">
                          Sin leads
                        </p>
                      )}
                      {col.forms.map((form) => (
                        <LeadCardDraggable
                          key={form.id}
                          id={form.id}
                          disabled={updating === form.id || !!form.convertedToEventId}
                        >
                          {({ attributes, listeners, setNodeRef, transform, isDragging: cardIsDragging }) => (
                            <div
                              ref={(node) => {
                                setNodeRef(node);
                                cardRefs.current[form.id] = node;
                              }}
                              data-lead-id={form.id}
                              {...attributes}
                              {...listeners}
                              className={`border border-zinc-200/70 dark:border-zinc-700/60 border-l-4 rounded-lg p-3 ${
                                form.convertedToEventId ? "cursor-pointer" : "cursor-grab active:cursor-grabbing"
                              } transition-all duration-200 ${
                                cardIsDragging
                                  ? "opacity-0 pointer-events-none"
                                  : "shadow-sm hover:shadow-md"
                              } ${updating === form.id ? "opacity-50" : ""} ${
                                lastViewedId === form.id
                                  ? "bg-zinc-100 dark:bg-zinc-700/60 ring-2 ring-[#1b4d2e]/60"
                                  : "bg-white dark:bg-zinc-800/50"
                              }`}
                              style={{
                                transform: CSS.Translate.toString(transform),
                                borderLeftColor: form.convertedToEventId ? "#16a34a" : styles.hex,
                              }}
                              onClick={() => {
                                if (!cardIsDragging) navigate(leadDetailPath(form.id));
                              }}
                            >
                              <LeadCard
                                form={form}
                                isViewer={isViewer}
                                recurrencia={recurrenciaDe(form)}
                                nombreEspacio={nombreEspacio}
                                linkedEvent={
                                  form.convertedToEventId
                                    ? (contextEvents || []).find((e) => e.id === form.convertedToEventId)
                                    : null
                                }
                                menuSlot={
                                  <div className="relative">
                                    <button
                                      className="p-0.5 rounded text-zinc-300 hover:text-zinc-600 hover:bg-zinc-100"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setCardMenuId(cardMenuId === form.id ? null : form.id);
                                      }}
                                    >
                                      <EllipsisVerticalIcon className="h-4 w-4" />
                                    </button>
                                    {cardMenuId === form.id && (
                                      <>
                                        <div className="fixed inset-0 z-10" onClick={(e) => { e.stopPropagation(); setCardMenuId(null); }} />
                                        <div className="absolute right-0 top-6 z-20 w-44 rounded-lg bg-white shadow-lg ring-1 ring-zinc-200 py-1 text-xs dark:bg-zinc-800 dark:ring-zinc-700">
                                          <button
                                            className="flex w-full items-center gap-2 px-3 py-2 text-zinc-700 hover:bg-zinc-50 dark:text-zinc-200 dark:hover:bg-zinc-700"
                                            onClick={(e) => { e.stopPropagation(); setCardMenuId(null); navigate(leadDetailPath(form.id)); }}
                                          >
                                            <PencilSquareIcon className="h-3.5 w-3.5 text-zinc-400" />
                                            {soloLectura ? "Ver" : "Ver / Editar"}
                                          </button>
                                          {/* Mover y eliminar escriben: fuera en solo lectura. */}
                                          {!soloLectura && (
                                          <>
                                          <div className="border-t border-zinc-100 dark:border-zinc-700 mt-1 pt-1">
                                            <span className="flex items-center gap-2 px-3 py-1.5 text-zinc-400 font-medium">
                                              <ArrowRightIcon className="h-3.5 w-3.5" />
                                              Mover a
                                            </span>
                                            {columnsConfig
                                              .filter((c) => c.key !== (form.status || "pendiente"))
                                              .map((c) => {
                                                const cStyles = COLOR_PALETTE[c.color] || COLOR_PALETTE.gray;
                                                return (
                                                  <button
                                                    key={c.key}
                                                    className="flex w-full items-center gap-2 px-3 py-1.5 pl-8 text-zinc-700 hover:bg-zinc-50 dark:text-zinc-200 dark:hover:bg-zinc-700"
                                                    onClick={(e) => {
                                                      e.stopPropagation();
                                                      setCardMenuId(null);
                                                      handleStatusChange(form.id, c.key);
                                                    }}
                                                  >
                                                    <span className={`inline-block w-2 h-2 rounded-full ${cStyles.dot}`} />
                                                    {c.label}
                                                  </button>
                                                );
                                              })}
                                          </div>
                                          <div className="border-t border-zinc-100 dark:border-zinc-700 mt-1 pt-1">
                                            <button
                                              className="flex w-full items-center gap-2 px-3 py-2 text-red-600 hover:bg-red-50"
                                              onClick={(e) => { e.stopPropagation(); setCardMenuId(null); navigate(leadDetailPath(form.id, { delete: "1" })); }}
                                            >
                                              <TrashIcon className="h-3.5 w-3.5" />
                                              Eliminar
                                            </button>
                                          </div>
                                          </>
                                          )}
                                        </div>
                                      </>
                                    )}
                                  </div>
                                }
                              />
                            </div>
                          )}
                        </LeadCardDraggable>
                      ))}
                    </div>
                  )}
                </ColumnDropZone>
              </div>
            );
          })}
          </div>
          {/* Edge fade gradients — hint that there's more content to scroll */}
          <div
            className={`pointer-events-none absolute left-0 top-0 bottom-2 sm:bottom-4 w-6 bg-gradient-to-r from-white dark:from-zinc-900 to-transparent transition-opacity duration-200 ${
              scrollEdges.left ? "opacity-100" : "opacity-0"
            }`}
          />
          <div
            className={`pointer-events-none absolute right-0 top-0 bottom-2 sm:bottom-4 w-6 bg-gradient-to-l from-white dark:from-zinc-900 to-transparent transition-opacity duration-200 ${
              scrollEdges.right ? "opacity-100" : "opacity-0"
            }`}
          />
        </div>
        <DragOverlay dropAnimation={null}>
          {activeDragId ? (() => {
            const f = forms.find((x) => x.id === activeDragId);
            if (!f) return null;
            const colColor = COLOR_PALETTE[
              (columnsConfig.find((c) => c.key === (f.status || "pendiente")) || {}).color || "gray"
            ];
            const linkedEv = f.convertedToEventId
              ? (contextEvents || []).find((e) => e.id === f.convertedToEventId)
              : null;
            return (
              <div
                className="border border-zinc-200/70 dark:border-zinc-700/60 border-l-4 rounded-lg p-3 shadow-2xl ring-2 ring-[#1b4d2e]/40 bg-white dark:bg-zinc-800/90 cursor-grabbing"
                style={{
                  borderLeftColor: f.convertedToEventId ? "#16a34a" : colColor.hex,
                  width: dragWidth ? `${dragWidth}px` : undefined,
                }}
              >
                <LeadCard
                  form={f}
                  isViewer={isViewer}
                  recurrencia={recurrenciaDe(f)}
                  nombreEspacio={nombreEspacio}
                  linkedEvent={linkedEv}
                />
              </div>
            );
          })() : null}
        </DragOverlay>
      </DndContext>
      )}

      {/* ── Calendar view ─────────────────────────────────────── */}
      {leadsTab === "calendario" && (
        <div className="max-w-6xl mx-auto w-full flex-1 min-h-0 overflow-y-auto">
          {/* Year nav */}
          <div className="flex items-center justify-center gap-4 mb-4">
            <button onClick={() => { setCalendarYear((y) => y - 1); closePopover(); }} className="p-1.5 rounded-lg text-zinc-500 hover:bg-zinc-100 dark:hover:bg-zinc-700 transition">
              <span className="text-sm font-semibold">&#9664;</span>
            </button>
            <span className="text-lg font-bold text-zinc-900 dark:text-white">{calendarYear}</span>
            <button onClick={() => { setCalendarYear((y) => y + 1); closePopover(); }} className="p-1.5 rounded-lg text-zinc-500 hover:bg-zinc-100 dark:hover:bg-zinc-700 transition">
              <span className="text-sm font-semibold">&#9654;</span>
            </button>
          </div>
          {/* Legend */}
          <div className="flex items-center justify-center gap-4 mb-4 text-xs text-zinc-500">
            <span className="flex items-center gap-1.5"><span className="inline-block h-3 w-3 rounded" style={{ borderTop: "12px solid #fbbf24", borderLeft: "12px solid transparent", width: 0, height: 0 }} /> Leads interesados</span>
            <span className="flex items-center gap-1.5"><span className="inline-block h-3 w-3 rounded bg-red-400/70" /> Evento confirmado</span>
            <span className="flex items-center gap-1.5"><span className="inline-block h-3 w-3 rounded" style={{ background: "linear-gradient(to top, #f87171 50%, transparent 50%)" }} /> Ocupación parcial</span>
          </div>
          {/* Retícula de 12 meses, compartida con la vista Año del
              calendario. El relleno de cada día es la proporción de espacios
              ocupados: antes bastaba un evento en un salón para pintar el día
              entero de rojo y el año se veía lleno estando casi vacío. */}
          <MiniYearCalendar
            year={calendarYear}
            dayInfo={leadsDayInfo}
            onDayClick={(iso, rect) =>
              showPopover(iso, rect, interestCalendarData.get(iso), {
                inmediato: true,
              })
            }
            onDayEnter={(iso, rect) =>
              showPopover(iso, rect, interestCalendarData.get(iso))
            }
            onDayLeave={scheduleHidePopover}
          />
        </div>
      )}

      {/* ── Stats view ─────────────────────────────────────── */}
      {leadsTab === "stats" && (
        <div className="max-w-6xl mx-auto w-full flex-1 min-h-0 overflow-y-auto">
          <LeadStats
            forms={forms}
            columnsConfig={columnsConfig}
            venueId={selectedVenueId}
            venue={selectedVenue}
          />
        </div>
      )}

      {/* ── Day popover (calendar) ──────────────────────────── */}
      {dayPopover && createPortal(
        <div
          className="fixed z-[95] w-64 max-h-72 overflow-y-auto rounded-xl border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 shadow-xl p-3 space-y-2"
          style={{ left: dayPopover.x, top: dayPopover.y }}
          onMouseEnter={cancelHidePopover}
          onMouseLeave={scheduleHidePopover}
        >
            <div className="text-xs font-semibold text-zinc-500">{dayPopover.key}</div>
            {dayPopover.data.leads.length > 0 && (
              <div className="space-y-1">
                <div className="text-[10px] uppercase tracking-wide text-zinc-400 px-2">Leads interesados</div>
                {dayPopover.data.leads.map((lead, i) => {
                  const col = columnsConfig.find((c) => c.key === lead.status);
                  const palette = COLOR_PALETTE[col?.color] || COLOR_PALETTE.gray;
                  // Por id: dos leads con el mismo nombre y estado abrían el equivocado.
                  const fullLead = forms.find((f) => f.id === lead.id);
                  return (
                    <button
                      key={i}
                      className="flex items-center gap-2 w-full text-left rounded-lg px-2 py-1.5 hover:bg-zinc-50 dark:hover:bg-zinc-700/50 transition text-sm"
                      onClick={() => { if (fullLead) { closePopover(); navigate(leadDetailPath(fullLead.id)); } }}
                    >
                      <span className={`inline-block w-2 h-2 rounded-full flex-shrink-0 ${palette.dot}`} />
                      <span className="text-zinc-800 dark:text-zinc-200 truncate">{lead.name}</span>
                      <span className={`ml-auto text-[10px] font-medium px-1.5 py-0.5 rounded-full flex-shrink-0 ${palette.badge}`}>{col?.label || lead.status}</span>
                    </button>
                  );
                })}
              </div>
            )}
            {dayPopover.data.events.length > 0 && (
              <div className="space-y-1">
                <div className="text-[10px] uppercase tracking-wide text-zinc-400 px-2">Eventos</div>
                {dayPopover.data.events.map((ev, i) => (
                  <button
                    key={i}
                    className="flex items-start gap-2 w-full text-left rounded-lg px-2 py-1.5 hover:bg-zinc-50 dark:hover:bg-zinc-700/50 transition text-sm"
                    onClick={() => { closePopover(); navigate(`/dashboard/eventos/${ev.id}`, { state: { from: "leads-calendario" } }); }}
                  >
                    <span className="inline-block w-2 h-2 mt-1.5 rounded-full bg-red-400 flex-shrink-0" />
                    <span className="min-w-0 flex-1">
                      <span className="block text-zinc-800 dark:text-zinc-200 truncate">{ev.name}</span>
                      {dayPopover.data.espaciosTotal > 0 && (
                        <span className="block text-[10px] text-zinc-400 truncate">
                          {ev.espacios?.length > 0
                            ? ev.espacios.join(", ")
                            : "Todo el venue"}
                        </span>
                      )}
                    </span>
                    <ArrowTopRightOnSquareIcon className="mt-0.5 h-3.5 w-3.5 text-zinc-400 flex-shrink-0" />
                  </button>
                ))}
                {dayPopover.data.espaciosTotal > 0 && (
                  <div className="px-2 pt-1 text-[10px] text-zinc-400">
                    {dayPopover.data.espaciosLibres.length > 0
                      ? `Libres: ${dayPopover.data.espaciosLibres.join(", ")}`
                      : "Sin espacios libres este día"}
                  </div>
                )}
              </div>
            )}
        </div>,
        document.body,
      )}

      {/* ── Modal: create lead ──────────────────────────────── */}
      {/* Paso 1 de crear lead: el cliente. El mismo diálogo del evento, con
          sus coincidencias, para elegirlo o darlo de alta. */}
      <ClienteEventoDialog
        open={eligiendoCliente}
        datosDelEvento={{}}
        titulo="Cliente del lead"
        subtitulo="A quién pertenece este lead: elígelo del directorio o dalo de alta"
        textoCrear="Crear cliente y continuar"
        origenCreacion="lead"
        onClose={() => { if (!leadCliente) setNewLead(EMPTY_LEAD); setEligiendoCliente(false); if (leadCliente) setShowCreateDialog(true); }}
        onLigar={(clienteId, { cliente } = {}) => {
          setLeadCliente(cliente || { id: clienteId });
          setEligiendoCliente(false);
          setShowCreateDialog(true);
        }}
      />
      {showCreateDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/30"
            onClick={cerrarCreacion}
          />
          <div className="relative w-full max-w-md rounded-xl border border-zinc-200 bg-white shadow-2xl dark:border-zinc-700/60 dark:bg-zinc-900 flex flex-col max-h-[90vh]">
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-200 dark:border-zinc-700/60">
              <div className="flex items-center gap-3">
                <div
                  className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg"
                  style={{ backgroundColor: ACCENT + "1a" }}
                >
                  <PlusIcon className="h-4 w-4" style={{ color: ACCENT }} />
                </div>
                <h2 className="text-base font-semibold text-zinc-900 dark:text-zinc-100">Crear nuevo lead</h2>
              </div>
              <button
                onClick={cerrarCreacion}
                className="p-1.5 rounded-lg text-zinc-400 hover:text-zinc-600 hover:bg-zinc-100 dark:hover:bg-zinc-700"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto px-5 py-5 space-y-4">
              {/* El nombre del lead es su título en el tablero y en su
                  página (`topic`); va primero, que es lo que uno viene a
                  escribir. */}
              <div>
                <label className="block text-xs font-medium text-zinc-500 mb-1">Nombre del lead</label>
                <Input
                  autoFocus
                  value={newLead.topic}
                  onChange={(e) => setNewLead((p) => ({ ...p, topic: e.target.value }))}
                  placeholder="Boda Esteban & Camila, Graduación 2027…"
                />
              </div>
              {/* El cliente ya se eligió en el paso anterior: aquí solo se
                  muestra, con la salida para cambiarlo. */}
              <div>
                <label className="block text-xs font-medium text-zinc-500 mb-1">Cliente</label>
                <div className="flex items-start gap-3 rounded-lg border border-zinc-200 dark:border-zinc-700/60 bg-zinc-50 dark:bg-zinc-800/40 px-3 py-2.5">
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-zinc-900 dark:text-white truncate">
                      {leadCliente ? nombreCliente(leadCliente) : "Sin cliente"}
                    </div>
                    {leadCliente && (
                      <div className="text-xs text-zinc-500 break-words">
                        {[contactoCliente(leadCliente), leadCliente.correo, leadCliente.telefono]
                          .filter(Boolean)
                          .join(" · ") || "Sin datos de contacto"}
                      </div>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => { setShowCreateDialog(false); setEligiendoCliente(true); }}
                    className="shrink-0 text-xs font-semibold hover:underline"
                    style={{ color: ACCENT }}
                  >
                    Cambiar
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-zinc-500 mb-1">Notas</label>
                <Textarea
                  value={newLead.notes}
                  onChange={(e) => setNewLead((p) => ({ ...p, notes: e.target.value }))}
                  placeholder="Detalles del lead..."
                  rows={3}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-zinc-500 mb-1">Origen</label>
                <Input
                  list="origin-suggestions"
                  value={newLead.origin}
                  onChange={(e) => setNewLead((p) => ({ ...p, origin: e.target.value }))}
                  placeholder="Instagram, Referido, Llamada..."
                />
                <datalist id="origin-suggestions">
                  {existingOrigins.map((o) => (
                    <option key={o} value={o} />
                  ))}
                </datalist>
              </div>
            </div>

            {/* Footer */}
            <div className="flex justify-end gap-2 border-t border-zinc-200 dark:border-zinc-700/60 px-5 py-4">
              <button onClick={cerrarCreacion} className="px-4 py-2 rounded-lg text-sm font-semibold text-zinc-700 dark:text-zinc-300 bg-zinc-100 dark:bg-zinc-800 hover:bg-zinc-200 dark:hover:bg-zinc-700 transition">
                Cancelar
              </button>
              <button onClick={handleCreateLead} disabled={creating} className="px-4 py-2 rounded-lg text-sm font-semibold text-white shadow-sm transition hover:opacity-90 disabled:opacity-50" style={{ backgroundColor: ACCENT }}>
                {creating ? "Creando..." : "Crear lead"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Dialog: column settings ──────────────────────────── */}
      <Dialog
        open={showSettingsDialog}
        onClose={() => setShowSettingsDialog(false)}
      >
        <DialogTitle>Configurar columnas</DialogTitle>
        <DialogBody>
          <div className="space-y-3">
            {editColumns.map((col, idx) => {
              const isSystem = SYSTEM_COLUMN_KEYS.has(col.key);
              return (
              <div key={idx} className="flex items-center gap-2">
                {/* Reorder buttons */}
                <div className="flex flex-col -space-y-1">
                  <button
                    type="button"
                    disabled={idx === 0}
                    onClick={() => {
                      const next = [...editColumns];
                      [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
                      setEditColumns(next);
                    }}
                    className="p-0.5 text-zinc-400 hover:text-zinc-600 disabled:opacity-30 disabled:cursor-not-allowed"
                    title="Mover arriba"
                  >
                    <ChevronUpIcon className="h-3.5 w-3.5" />
                  </button>
                  <button
                    type="button"
                    disabled={idx === editColumns.length - 1}
                    onClick={() => {
                      const next = [...editColumns];
                      [next[idx], next[idx + 1]] = [next[idx + 1], next[idx]];
                      setEditColumns(next);
                    }}
                    className="p-0.5 text-zinc-400 hover:text-zinc-600 disabled:opacity-30 disabled:cursor-not-allowed"
                    title="Mover abajo"
                  >
                    <ChevronDownIcon className="h-3.5 w-3.5" />
                  </button>
                </div>
                <Input
                  value={col.label}
                  onChange={(e) => {
                    if (isSystem) return;
                    const next = [...editColumns];
                    next[idx] = { ...next[idx], label: e.target.value };
                    setEditColumns(next);
                  }}
                  placeholder="Nombre de columna"
                  className="flex-1"
                  disabled={isSystem}
                />
                {/* Colour swatches */}
                <div className="flex items-center gap-1">
                  {COLOR_NAMES.map((c) => (
                    <button
                      key={c}
                      type="button"
                      onClick={() => {
                        const next = [...editColumns];
                        next[idx] = { ...next[idx], color: c };
                        setEditColumns(next);
                      }}
                      className={`w-5 h-5 rounded-full ${SWATCH_BG[c]} ${
                        col.color === c ? `ring-2 ${SWATCH_RING[c]} ring-offset-1` : ""
                      }`}
                      title={c}
                    />
                  ))}
                </div>
                {!ALWAYS_VISIBLE_KEYS.has(col.key) ? (
                  <button
                    type="button"
                    onClick={() => {
                      const next = [...editColumns];
                      next[idx] = { ...next[idx], hidden: !next[idx].hidden };
                      setEditColumns(next);
                    }}
                    className={`p-1 ${col.hidden ? "text-zinc-300" : "text-zinc-500"} hover:text-zinc-700`}
                    title={col.hidden ? "Mostrar columna" : "Ocultar columna"}
                  >
                    {col.hidden
                      ? <EyeSlashIcon className="h-4 w-4" />
                      : <EyeIcon className="h-4 w-4" />}
                  </button>
                ) : (
                  <span className="p-1 text-zinc-300" title="Siempre visible">
                    <EyeIcon className="h-4 w-4" />
                  </span>
                )}
                {!isSystem ? (
                  <button
                    type="button"
                    onClick={() => setEditColumns(editColumns.filter((_, i) => i !== idx))}
                    className="p-1 text-gray-400 hover:text-red-500"
                    title="Eliminar columna"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                ) : (
                  <span className="p-1 w-6" />
                )}
              </div>
              );
            })}

            <button
              type="button"
              onClick={() =>
                setEditColumns([
                  ...editColumns,
                  { key: "", label: "", color: "gray" },
                ])
              }
              className="flex items-center gap-1 text-sm font-medium"
              style={{ color: ACCENT }}
            >
              <PlusIcon className="h-4 w-4" /> Agregar columna
            </button>

            <div className="flex justify-end gap-2 pt-2">
              <button onClick={() => setShowSettingsDialog(false)} className="px-4 py-2 rounded-lg text-sm font-semibold text-zinc-700 dark:text-zinc-300 bg-zinc-100 dark:bg-zinc-800 hover:bg-zinc-200 dark:hover:bg-zinc-700 transition">
                Cancelar
              </button>
              <button onClick={handleSaveColumns} disabled={savingColumns} className="px-4 py-2 rounded-lg text-sm font-semibold text-white shadow-sm transition hover:opacity-90 disabled:opacity-50" style={{ backgroundColor: ACCENT }}>
                {savingColumns ? "Guardando..." : "Guardar"}
              </button>
            </div>
          </div>
        </DialogBody>
      </Dialog>
    </div>
  );
};

export default Formularios;
